<template>
  <div class="mini-tab-content">
    <div class="interc-toolbar">
      <span class="interc-toolbar__title">
        <feather-icon
          icon="MapPinIcon"
          size="13"
          class="mr-25 text-success"
        />
        {{ activeDestination ? 'Destino definido' : 'Nenhum destino' }}
      </span>
      <button
        v-if="!activeDestination && $hasAbility('regulacao.gerenciar')"
        class="btn btn-sm btn-success"
        @click="openDestinationModal"
      >
        <feather-icon
          icon="PlusIcon"
          size="13"
          class="mr-25"
        />
        Definir destino
      </button>
    </div>

    <div
      v-if="!involvedDestinations.length"
      class="interc-empty"
    >
      <feather-icon
        icon="MapPinIcon"
        size="26"
      />
      <p>Nenhum destino registrado.</p>
    </div>

    <div
      v-if="involvedDestinations.length"
      class="interc-list"
    >
      <div
        v-for="dest in involvedDestinations"
        :key="dest.id"
        :class="['outcome-item', { 'outcome-item--cancelled': dest.cancelled_at }]"
      >
        <div class="interc-item__top">
          <div class="outcome-item__badge">
            <feather-icon
              :icon="dest.cancelled_at ? 'XCircleIcon' : 'MapPinIcon'"
              size="11"
              class="mr-25"
            />
            {{ dest.establishment ? dest.establishment.establishment_name : '—' }}
          </div>
          <button
            v-if="!dest.cancelled_at && $hasAbility('regulacao.gerenciar')"
            class="interc-item__del"
            title="Cancelar destino"
            @click="cancelDestination(dest.id)"
          >
            <feather-icon
              icon="Trash2Icon"
              size="13"
            />
          </button>
        </div>
        <p
          v-if="dest.establishment"
          class="interc-item__desc"
        >
          <feather-icon
            icon="NavigationIcon"
            size="11"
            class="mr-25"
          />
          {{ formatAddress(dest.establishment) }}
        </p>
        <p
          v-if="dest.observation"
          class="interc-item__desc"
        >
          {{ dest.observation }}
        </p>
        <div class="interc-item__meta">
          <feather-icon
            icon="UserIcon"
            size="11"
          />
          <span>{{ dest.registered_by ? dest.registered_by.name : '—' }}</span>
          <span class="interc-item__dot" />
          <feather-icon
            icon="ClockIcon"
            size="11"
          />
          <span>{{ dest.created_at | formatDate }}</span>
        </div>
        <div
          v-if="dest.cancelled_at"
          class="interc-item__cancelled-row"
        >
          <feather-icon
            icon="XCircleIcon"
            size="11"
            class="mr-25"
          />
          Cancelado por
          <strong>{{ dest.cancelled_by ? dest.cancelled_by.name : '—' }}</strong>
          · {{ dest.cancelled_at | formatDate }}
        </div>
      </div>
    </div>

    <b-modal
      v-model="showDestinationModal"
      title="Definir Destino"
      size="md"
      centered
      no-close-on-backdrop
      @hidden="resetForm"
    >
      <div class="form-group mb-2">
        <label class="interc-label">Tipo de Estabelecimento</label>
        <v-select
          v-model="filterTypeId"
          :options="establishmentTypes"
          :reduce="(t) => t.id"
          label="establishment_type_name"
          placeholder="Filtrar por tipo (opcional)..."
          class="vselect"
          @input="loadEstablishments"
        />
      </div>
      <div
        v-if="loadingEstablishments"
        class="interc-loading"
      >
        <b-spinner
          small
          variant="success"
        />
        <span>Carregando...</span>
      </div>
      <template v-else>
        <div class="form-group mb-2">
          <label class="interc-label"><span class="required">*</span> Estabelecimento</label>
          <v-select
            v-model="newDestination.establishment_id"
            :options="establishments"
            :reduce="(e) => e.id"
            label="establishment_name"
            placeholder="Selecione o estabelecimento..."
            class="vselect"
          />
        </div>
        <div class="form-group mb-0">
          <label class="interc-label">Observação</label>
          <textarea
            v-model="newDestination.observation"
            class="form-control"
            rows="3"
            placeholder="Observação opcional..."
            maxlength="2000"
          />
        </div>
      </template>

      <template #modal-footer>
        <button
          class="btn btn-sm btn-outline-secondary mr-1"
          @click="showDestinationModal = false"
        >
          Fechar
        </button>
        <button
          class="btn btn-sm btn-success"
          :disabled="savingDestination || !newDestination.establishment_id"
          @click="saveDestination"
        >
          <b-spinner
            v-if="savingDestination"
            small
            class="mr-25"
          />
          <feather-icon
            v-else
            icon="CheckCircleIcon"
            size="13"
            class="mr-25"
          />
          Definir
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import involvedPanelMixin from './involvedPanelMixin'

export default {
  name: 'DestinationPanel',
  mixins: [involvedPanelMixin],
  props: {
    initialDestinations: { type: Array, default: () => [] },
  },
  data() {
    return {
      involvedDestinations: [...(this.initialDestinations || [])],
      establishmentTypes: [],
      establishments: [],
      loadingEstablishments: false,
      savingDestination: false,
      showDestinationModal: false,
      filterTypeId: null,
      newDestination: { establishment_id: null, observation: '' },
    }
  },
  computed: {
    activeDestination() {
      return this.involvedDestinations.find(d => !d.cancelled_at) || null
    },
  },
  methods: {
    formatAddress(establishment) {
      const parts = [];
      let street = establishment.address || '';
      if (establishment.number) street += `, ${establishment.number}`;
      if (street) parts.push(street);
      if (establishment.complement) parts.push(establishment.complement);
      if (establishment.neighborhood) parts.push(establishment.neighborhood);

      const city = establishment.city ? establishment.city.name : '';
      const state = establishment.state ? establishment.state.code : '';
      const cityState = [city, state].filter(Boolean).join('/');
      if (cityState) parts.push(cityState);

      if (establishment.zip_code) parts.push(`CEP ${establishment.zip_code}`);

      return parts.join(' - ') || '—';
    },
    openDestinationModal() {
      this.showDestinationModal = true
      this.loadEstablishmentTypes()
      this.loadEstablishments()
    },
    resetForm() {
      this.newDestination = { establishment_id: null, observation: '' }
      this.filterTypeId = null
    },
    loadEstablishmentTypes() {
      this.$http
        .get(`${this.$store.state.api}establishment_types?paginated=false&status=1`)
        .then(r => { this.establishmentTypes = r.data.data || [] })
        .catch(() => { this.$message('Erro', 'Não foi possível carregar os tipos de estabelecimento.', 'error') })
    },
    loadEstablishments() {
      this.loadingEstablishments = true
      this.newDestination.establishment_id = null
      let url = `${this.$store.state.api}establishments?paginated=false&status=1`
      if (this.filterTypeId) url += `&type_establishment_id=${this.filterTypeId}`
      this.$http
        .get(url)
        .then(r => { this.establishments = r.data.data || [] })
        .catch(() => { this.$message('Erro', 'Não foi possível carregar os estabelecimentos.', 'error') })
        .finally(() => { this.loadingEstablishments = false })
    },
    async saveDestination() {
      this.savingDestination = true
      try {
        const res = await this.$http.post(`${this.$store.state.api}occurrence_involved_destinations`, {
          occurrence_involved_id: this.occurrenceInvolvedId,
          establishment_id: this.newDestination.establishment_id,
          observation: this.newDestination.observation || null,
        })
        const saved = res.data.data || res.data
        this.involvedDestinations.push(saved)
        this.showDestinationModal = false
        this.$emit('destination-saved', this.involvedDestinations)
        this.$message('Destino definido', 'Destino salvo com sucesso.', 'success')
      } catch (e) {
        this.$message('Erro', 'Erro ao definir destino.', 'error')
      } finally {
        this.savingDestination = false
      }
    },
    async cancelDestination(id) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'O destino ficará marcado como cancelado e será mantido no histórico. Deseja continuar?',
        {
          title: 'Cancelar destino', okTitle: 'Sim, cancelar', cancelTitle: 'Voltar', okVariant: 'danger', centered: true, size: 'sm',
        },
      )
      if (!confirmed) return
      try {
        const res = await this.$http.delete(`${this.$store.state.api}occurrence_involved_destinations/${id}`)
        const updated = res.data.data || res.data
        const idx = this.involvedDestinations.findIndex(d => d.id === id)
        if (idx !== -1) this.$set(this.involvedDestinations, idx, updated)
        this.$emit('destination-saved', this.involvedDestinations)
      } catch (e) {
        this.$message('Erro', 'Erro ao cancelar destino.', 'error')
      }
    },
  },
}
</script>
